import { useCallback } from 'react';
import styled from '@emotion/styled';
import { Button, message, Popover, Table, TableColumnsType, TableProps, Tag, Tooltip } from 'antd';
import StatusButton from 'components/StatusButton';
import { getStoreCount, StoreScopesPreview } from 'components/StoreScopes';
import { useTranslate, TFnType } from '@tolgee/react';
import { deleteMarketingTask, fetchMarketingTask, updateMarketingTask } from 'services/api/oms';
import { PreviewWrapper } from 'views/Assistant/Department/components/AssignReceptionConfColumn';
import StoreList from '../components/StoreList';
import { getUpdateTaskValues } from './Task';
import { Question } from './PolicyForm';
import ConflictSwitch from '../components/ConflictSwitch';
import { isNoRegionScenario } from './config';
import { ALL2all } from './TaskForm/StoreScopesField';
import moreSvg from '../assets/more.svg';

export const DefaultTag = styled(Tag)`
  margin-left: 8px;
  background: transparent;
  cursor: pointer;
`;

const FirstButton = styled(Button)`
  padding-left: 0;
`;

const Trigger = styled.span`
  cursor: pointer;
  > img {
    width: 18px;
    margin-left: 4px;
  }
`;

type RT = Util.ElementType<Marketing.Scenario['tasks']>;

export interface TaskTableProps extends TableProps<RT> {
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  region?: General.UpperCaseRegion;
  onCheckDetail: (record: RT) => void;
  onUpdated?: () => void;
}

export const useGetTaskName = () => {
  const { t } = useTranslate();

  const getTaskName = useCallback(
    (name: string) => {
      return t({
        key: `defaultTaskName.${name}`,
        defaultValue: name,
      });
    },
    [t],
  );

  return {
    t,
    getTaskName,
  };
};

export const regionStoresRender = (
  regionStores: Marketing.RegionStore[],
  platform: General.PlatformUpperCase,
  t: TFnType,
) => {
  const scopes: General.StoreScope[] =
    regionStores.length > 0
      ? [
          {
            platform,
            storeCount: 0,
            regionStoreScopes: ALL2all(regionStores),
          },
        ]
      : [];

  return (
    <Popover
      content={
        <PreviewWrapper>
          <StoreScopesPreview value={scopes} showSearch={false} showTitle={false} />
        </PreviewWrapper>
      }
    >
      <Trigger>
        {t('aide.x 个店铺', { x: getStoreCount(scopes) })}
        <img src={moreSvg} alt="more" />
      </Trigger>
    </Popover>
  );
};

function TaskTable(props: TaskTableProps) {
  const { scenarioType, platform, region, onCheckDetail, onUpdated, ...otherProps } = props;

  const { t, getTaskName } = useGetTaskName();

  const columns: TableColumnsType<RT> = [
    {
      key: 'taskName',
      dataIndex: 'taskName',
      title: t('marketing.任务名称'),
      width: 150,
      render: (taskName: RT['taskName'], record: RT) => {
        const { isDefault } = record;

        if (!isDefault) {
          return taskName;
        }

        const formatTaskName = getTaskName(taskName);
        return (
          <div>
            {formatTaskName}
            <Tooltip
              title={t(
                'aide.新授权店铺会自动添加至默认任务中，当店铺从默认任务或其他任务中移除时，不会再自动加入默认任务中',
              )}
            >
              <DefaultTag color="blue">{t('marketing.默认')}</DefaultTag>
            </Tooltip>
          </div>
        );
      },
    },
    {
      key: 'stores',
      dataIndex: 'stores',
      title: t('aide.应用店铺'),
      width: 120,
      render: (_, record: RT) => {
        const { stores = [], regionStores = [] } = record;

        if (isNoRegionScenario(scenarioType)) {
          return regionStoresRender(regionStores, platform, t);
        }

        const storeList = stores.map(({ storeId, alias, storeName }) => {
          return {
            id: storeId,
            alias,
            storeName,
          };
        });

        return <StoreList isStoreAliasFirst storeList={storeList} />;
      },
    },
    ...(scenarioType === 'RECEPTION_SCORE'
      ? []
      : [
          {
            key: 'rounds',
            dataIndex: 'rounds',
            title: (
              <div>
                {t('aide.有效策略')}
                <Tooltip title={t('aide.有效策略指生效中和未开始的策略')}>
                  <Question />
                </Tooltip>
              </div>
            ),
            width: 120,
            render: (rounds: RT['rounds']) => {
              const [{ totalPolicy = 0, validPolicy = 0 } = {}] = rounds || [];

              return `${validPolicy}/${totalPolicy}`;
            },
          },
        ]),
    {
      key: 'enable',
      dataIndex: 'enable',
      title: t('aide.任务开关'),
      width: 100,
      render: (enable: RT['enable'], record: RT) => {
        const { taskId } = record;

        return (
          <ConflictSwitch
            platform={platform}
            region={region}
            scenarioType={scenarioType}
            taskId={taskId}
            checked={enable}
            onChange={(enable) => {
              return fetchMarketingTask(taskId)
                .then((res) => {
                  return updateMarketingTask(
                    getUpdateTaskValues({ ...res, enable }, scenarioType, platform, region),
                  );
                })
                .then(() => {
                  if (onUpdated) {
                    onUpdated();
                  }

                  message.success(t('operation.saveSucceed'));
                })
                .catch(() => {
                  message.error(t('operation.saveFailed'));
                });
            }}
            checkedChildren={t('aide.开')}
            unCheckedChildren={t('aide.关')}
          />
        );
      },
    },
    {
      key: 'operation',
      title: t('操作'),
      width: 120,
      render: (_, record: RT) => {
        const { taskId, isDefault } = record;

        return (
          <div>
            <FirstButton type="link" onClick={() => onCheckDetail(record)}>
              {t('tableColumn.查看详情')}
            </FirstButton>
            {!isDefault && (
              <StatusButton
                type="link"
                popConfirmProps={{
                  placement: 'topRight',
                  title: t('marketing.你确定要删除当前任务吗'),
                  okText: t('marketing.删除'),
                  cancelText: t('marketing.取消'),
                }}
                onClick={() => {
                  return deleteMarketingTask(taskId)
                    .then(() => {
                      message.success(t('phrase.删除成功'));

                      if (onUpdated) {
                        onUpdated();
                      }
                    })
                    .catch(() => {
                      message.error(t('phrase.删除失败，请稍后再试'));
                    });
                }}
              >
                {t('marketing.删除')}
              </StatusButton>
            )}
          </div>
        );
      },
    },
  ];

  return <Table rowKey="taskId" columns={columns} {...otherProps} />;
}

export default TaskTable;
